/** Settings → Webhooks. */
import { useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DetailDrawer } from "@/components/ui/detail-drawer";
import { Plus, Trash2, FlaskConical, RotateCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ComingOnline, THead } from "./shared";
import {
  useWebhooks, useCreateWebhook, useDeleteWebhook, useTestWebhook,
  useWebhookDeliveries, useReplayDelivery,
  type Webhook,
} from "@/hooks/security/useEdrAdmin";

const EVENTS = [
  "alert.created", "alert.escalated", "alert.dismissed", "alert.resolved",
  "agent.classified", "agent.sanctioned",
  "approval.created", "approval.decided",
];

export function WebhooksSection() {
  const q = useWebhooks();
  const [addOpen, setAddOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const unavail = q.data?.kind === "unavailable";
  const hooks = q.data?.kind === "ok" ? q.data.data.webhooks : [];
  const del = useDeleteWebhook();
  const test = useTestWebhook();

  return (
    <Panel
      title={`Webhooks (${hooks.length})`}
      actions={<Button size="sm" disabled={unavail} onClick={() => setAddOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Add webhook</Button>}
      loading={q.isLoading}
      empty={!q.isLoading && (unavail || hooks.length === 0)}
      emptyTitle={unavail ? "Coming online — Q3 2026" : "No webhooks yet"}
      emptyDescription={unavail ? "Webhook endpoints aren't live yet." : "Configure outbound HTTPS hooks for alerts and agent events."}
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <THead cols={["Name", "URL", "Events", "Last delivery", ""]} />
          <tbody>
            {hooks.map((h) => (
              <tr key={h.id} className="border-b border-border/60 hover:bg-muted/40 cursor-pointer" onClick={() => setDrawerId(h.id)}>
                <td className="px-3 py-2 font-medium text-foreground">{h.name}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground truncate max-w-[280px]">{h.url}</td>
                <td className="px-3 py-2"><div className="flex flex-wrap gap-1">
                  {h.events.slice(0, 2).map((e) => <code key={e} className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">{e}</code>)}
                  {h.events.length > 2 && <span className="text-[10px] text-muted-foreground">+{h.events.length - 2}</span>}
                </div></td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums">
                  {h.lastDeliveryAt ? (
                    <span className={h.lastDeliveryOk ? "text-success" : "text-destructive"}>
                      {formatDistanceToNow(new Date(h.lastDeliveryAt), { addSuffix: true })} ({h.lastDeliveryStatus})
                    </span>
                  ) : "never"}
                </td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="h-7 px-2" disabled={test.isPending} onClick={() => test.mutate(h.id)}>
                    <FlaskConical className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { if (confirm(`Delete ${h.name}?`)) del.mutate(h.id); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddWebhookDialog open={addOpen} onOpenChange={setAddOpen} />
      <DeliveryDrawer id={drawerId} hook={hooks.find((h) => h.id === drawerId) ?? null} onClose={() => setDrawerId(null)} />
    </Panel>
  );
}

function AddWebhookDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [events, setEvents] = useState<Set<string>>(new Set(["alert.created"]));
  const create = useCreateWebhook();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a webhook</DialogTitle>
          <DialogDescription>HTTPS endpoint that receives selected events with an HMAC signature.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Slack alerts" /></div>
          <div className="space-y-1.5"><Label className="text-xs">URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.slack.com/services/…" /></div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Signing secret (HMAC)</Label>
              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() =>
                setSecret(crypto.getRandomValues(new Uint8Array(24)).reduce((s, b) => s + b.toString(16).padStart(2, "0"), ""))
              }><RotateCw className="h-3 w-3 mr-1" /> Generate</Button>
            </div>
            <Input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="paste or generate" /></div>
          <div className="space-y-1.5">
            <Label className="text-xs">Events</Label>
            <div className="grid grid-cols-2 gap-1.5 rounded border border-border p-2">
              {EVENTS.map((e) => (
                <label key={e} className="flex items-center gap-2 text-xs">
                  <Checkbox checked={events.has(e)} onCheckedChange={(c) => {
                    const next = new Set(events); c ? next.add(e) : next.delete(e); setEvents(next);
                  }} />
                  <code className="font-mono">{e}</code>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!name || !url || events.size === 0 || create.isPending} onClick={async () => {
            await create.mutateAsync({ name, url, secret: secret || undefined, events: Array.from(events) });
            onOpenChange(false);
            setName(""); setUrl(""); setSecret(""); setEvents(new Set(["alert.created"]));
          }}>{create.isPending ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryDrawer({ id, hook, onClose }: { id: string | null; hook: Webhook | null; onClose: () => void }) {
  const q = useWebhookDeliveries(id);
  const replay = useReplayDelivery();
  const deliveries = q.data?.deliveries ?? [];
  return (
    <DetailDrawer open={!!id} onOpenChange={(o) => !o && onClose()}
      title={hook?.name ?? "Webhook"} description={hook?.url}>
      <div className="space-y-4">
        <div>
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Recent deliveries</h3>
          {q.isLoading ? <p className="text-xs text-muted-foreground">Loading…</p> :
           deliveries.length === 0 ? <p className="text-xs text-muted-foreground">No deliveries yet.</p> :
            <table className="w-full text-xs">
              <THead cols={["Time", "Event", "Status", "Latency", ""]} />
              <tbody>
                {deliveries.slice(0, 50).map((d) => (
                  <tr key={d.id} className="border-b border-border/60">
                    <td className="px-3 py-2 text-muted-foreground tabular-nums">{formatDistanceToNow(new Date(d.ts), { addSuffix: true })}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{d.event}</td>
                    <td className="px-3 py-2"><span className={d.responseStatus < 400 ? "text-success" : "text-destructive"}>{d.responseStatus}</span></td>
                    <td className="px-3 py-2 tabular-nums">{d.latencyMs}ms</td>
                    <td className="px-3 py-2 text-right">
                      {d.responseStatus >= 400 && id && (
                        <Button size="sm" variant="ghost" className="h-6 text-[10px]" disabled={replay.isPending}
                          onClick={() => replay.mutate({ id, deliveryId: d.id })}>Replay</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </div>
      </div>
    </DetailDrawer>
  );
}
